<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col md="12">
          <h5 class="section-label mb-3">
            {{ $t('View Bills Wallet') }}
          </h5>
        </b-col>
      </b-row>
      <b-row>
        <b-col
          md="12"
        >
          <b-col
            md="3"
            style="font-size: 1.5em;"
            class="mb-1"
          >
            {{ $t('Balance') }}
          </b-col>
          <b-col
            md="7"
            style="font-size: 2.5em;"
          >
            <p :class="{'negative-balance': billsWalletBalance < 0}">
              {{ toBalance(billsWalletBalance) }}
            </p>
          </b-col>

          <div class="container">
            <div class="refresh-button">
              <Button
                v-if="getUserPermission('VIEW_BILLS_WALLET')"
                :label="$t('Refresh')"
                size="md"
                class="mt-3"
                :loading="isLoading"
                :disabled="isDisabled"
                @click="refetchAPI"
              />
            </div>
            <div class="topup-button">
              <Button
                :label="$t('Restock Bills Wallet')"
                size="md"
                class="mt-3"
                @click="initializePay"
              />
            </div>
          </div>
        </b-col>
      </b-row>

      <b-modal
        id="initialize-payment-modal"
        ref="initialize-payment-modal"
        :ok-title="$t('Top-Up')"
        :cancel-title="$t('Cancel')"
        cancel-variant="outline-secondary"
        :ok-disabled="isLoading"
        centered
        size="sm"
        no-close-on-backdrop
        :title="$t('Restock Bills Wallet')"
        @ok="handleSubmit"
        @hidden="resetModal"
      >
        <validation-observer ref="topupRules">
          <b-form @submit.prevent="handleSubmit">
            <b-row class="match-height">

              <b-col md="12">
                <InputNumberField
                  id="amount"
                  v-model="topupData.amount"
                  :label="$t('Amount')"
                  :placeholder="$t('Amount')"
                  type="number"
                  :error-name="$t('Amount')"
                  rules="required"
                />
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
        <template
          v-if="isReadonly"
          #modal-footer="{ }"
        >
          <div />
        </template>
      </b-modal>

    </div>

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import { modalMixins } from '@core/mixins/modal'
import { permissionMixin } from '@core/mixins/permission'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { tableMixins } from '@core/mixins/table'
import PaystackPop from '@paystack/inline-js'
import { positive, required } from '@validations'
import {
  BCard,
  BCol,
  BForm,
  BRow,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { mapActions, mapGetters } from 'vuex'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import { toBalance, toKobo } from '@/helpers/formatAmount'
import InputNumberField from '../../../forms/InputNumberField.vue'

import { eChannelsPermission } from '../../../../helpers/permissions'
import Button from '../../../components/button/Button.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BForm,
    Button,
    ValidationObserver,
    InputNumberField,
    FullScreenSpinner,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toBalance,
      isBusy: true,
      showSpinner: false,
      isLoading: false,
      isDisabled: false,
      sBusy: false,
      required,
      positive,
      endOfYearClosureList: [],
      billsWalletBalance: '',
      topupData: {
        amount: 0,
      },
      balanceRefreshTimeout: null,
    }
  },
  computed: {
    ...mapGetters({
      userData: 'auth/getCurrentUser',
    }),
  },
  beforeDestroy() {
    if (this.balanceRefreshTimeout) {
      clearTimeout(this.balanceRefreshTimeout)
      this.balanceRefreshTimeout = null
    }
  },
  async mounted() {
    this.fetchBillsWalletBalance()
  },
  methods: {
    ...mapActions({
      getBillsWalletBalance: 'eChannels/getBillsWalletBalance',
      initializePaymentWithAmount: 'admin/initializePaymentWithAmount',
    }),
    async fetchBillsWalletBalance() {
      try {
        this.showSpinner = true
        const response = await this.getBillsWalletBalance({
          permission: eChannelsPermission.VIEW_BILLS_WALLET,
        })
        const { data, status } = response
        if (status === 200 && data) {
          this.billsWalletBalance = data.balance
          this.showSpinner = false
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },
    initializePay() {
      this.$root.$emit('bv::show::modal', 'initialize-payment-modal')
    },
    handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.topupRules.validate().then(async success => {
        if (success) {
          const areYouSure = this.$t('Are you sure you want to Top-Up your wallet with')
          this.confirm(this.payWithPaystack, { text: `${areYouSure} ${toBalance(this.topupData.amount * 100)}?` })
        }
      })
    },
    async payWithPaystack() {
      try {
        this.hideModal('initialize-payment-modal')
        const formData = {
          amount: toKobo(this.topupData.amount),
          subscriptionType: 3,
        }
        const response = await this.initializePaymentWithAmount({
          formData,
          permission: eChannelsPermission.VIEW_BILLS_WALLET,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          const accessCode = data.data.access_code
          const popup = new PaystackPop()
          popup.resumeTransaction(accessCode, {
            onSuccess: () => {
              this.successAlert(this.$t('Wallet top-up successful'))
              this.balanceRefreshTimeout = setTimeout(() => {
                this.fetchBillsWalletBalance()
              }, 5000)
            },
            onCancel: () => {
              this.warningAlert(this.$t('Transaction was cancelled'))
            },
          })
        }
      } catch (error) {
        this.handleError(error)
      }
    },
    refetchAPI() {
      this.isLoading = true
      this.isDisabled = true
      this.fetchBillsWalletBalance()
      this.isLoading = false
      this.isDisabled = false
    },
    resetModal() {
      this.topupData = {
        amount: '',
      }
      this.isReadonly = false
      this.isLoading = false
    },
  },
}
</script>

<style scoped>
.negative-balance {
  color: red;
}
.container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
}
.refresh-button {
  text-align: left;
  flex: 1;
}
.topup-button {
  text-align: right;
  flex: 1;
}
</style>
